// eslint-disable-next-line no-restricted-imports
import { fromJS, OrderedMap } from '@gonfalon/immutable-shim/immutableShim';
import http, { jsonToImmutable, jsonToImmutableError } from '@gonfalon/legacy-http-client/httpUtils';
import type { ImmutableMap } from '@gonfalon/legacy-immutable/immutableUtils';
import { ApiVersions } from '@gonfalon/openapi/api-versions';
import { addHours } from 'date-fns';
import { normalize, schema } from 'normalizr';

import type { AccessToken, AccessTokenForm } from '#utils/accessTokenUtils';
import { createAccessToken as createAccessTokenRecord } from '#utils/accessTokenUtils';
import { convertMapToOrderedMap } from '#utils/collectionUtils';
import type { Link } from '#utils/linkUtils';
import { createJsonPatch } from '#utils/patchUtils';

type NormalizedAccessTokens = ImmutableMap<{
  items: AccessToken[];
  _links: { self: { href: string; type: string } };
}>;

export const createAccessToken = (token: AccessTokenForm) =>
  http
    .post('/api/v2/tokens', {
      body: token.toRep(),
    })
    .then(jsonToImmutable)
    .then(createAccessTokenRecord)
    .catch(jsonToImmutableError);

const accessTokens = new schema.Entity('accessTokens', {}, { idAttribute: '_id' });

type AccessTokensResponse = ImmutableMap<{
  entities: ImmutableMap<{
    accessTokens: OrderedMap<string, AccessToken>;
  }>;
  result: ImmutableMap<{
    _links: ImmutableMap<{ self: Link }>;
    items: string[];
  }>;
}>;

export const getAllAccessTokens = (url?: string): Promise<AccessTokensResponse> =>
  http
    .get(`${url || '/api/v2/tokens'}?showAll=1`, {
      headers: { 'Ld-Api-Version': ApiVersions.V3 },
    })
    .then((res) =>
      res.json().then((data) => {
        const normalized: NormalizedAccessTokens = fromJS(normalize(data, { items: [accessTokens] }));
        return normalized.withMutations((map) => {
          map.updateIn(['entities', 'accessTokens'], (tokens) =>
            convertMapToOrderedMap(tokens, normalized.getIn(['result', 'items'])),
          );
          map.updateIn(['entities', 'accessTokens'], (tokens) =>
            tokens ? tokens.map(createAccessTokenRecord) : OrderedMap(),
          );
        });
      }),
    )
    .catch(jsonToImmutableError);

export const updateAccessToken = (oldToken: AccessTokenForm, newToken: AccessTokenForm) => {
  const patch = createJsonPatch(oldToken.toRep(), newToken.toRep());
  return http
    .patch(oldToken.selfLink(), {
      body: patch,
    })
    .then(jsonToImmutable)
    .then(createAccessTokenRecord)
    .catch(jsonToImmutableError);
};

export const resetAccessToken = (token: AccessToken, expiry?: number) => {
  const baseUrl = `${token.selfLink()}/reset`;
  const url = expiry ? `${baseUrl}?expiry=${addHours(new Date(), expiry).valueOf()}` : baseUrl;

  return http.post(url).then(jsonToImmutable).then(createAccessTokenRecord).catch(jsonToImmutableError);
};

export const deleteAccessToken = (token: AccessToken) => http.delete(token.selfLink()).catch(jsonToImmutableError);
